import { useCallback } from 'react';
import sortBy from 'lodash/sortBy';

import useEvent from '@lyearn/core/hooks/useEvent';
import { splice } from '@lyearn/core/utils/immutable';
import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import { onPublishType } from '@/components/EditorBuilder/Settings/types';
import useNavigation from '@/hooks/useNavigation';
import config from '@/modules/content/config';
import { reorderItems } from '@/modules/corporate/components/DraggableList/utils';
import useDebouncedQuestionsUpdates from '@/modules/user-survey/views/UserSurveyEditor/hooks/useDebouncedQuestionsUpdates';
import routes from '@/pages/corporate/routes/contents/quiz';
import {
  PublishContentConfig,
  QuizSectionInput,
  ResourceStatusEnum,
  ResourceTypeEnum,
  ResourceVersionTypeEnum,
} from '@/types/schema';

import useRetakeQuiz from '../../../QuizDetails/hooks/useRetakeQuiz';
import { QuizEditorDetailsFragment } from '../../graphql/fragments/generated/QuizEditorContent';
import { adaptToQuestionInput } from '../../utils/adapters';
import { onQuizCreatorAction, QuizCreatorActionsEnum } from '../useQuizEditor/actions';
import useQuizEditorMutations from '../useQuizEditorMutations';
import useQuizEditorState from '../useQuizEditorState';
import usePublishQuiz from './usePublishQuiz';

const getSortedQuestions = (quizQuestions: QuizEditorDetailsFragment['quizQuestions']) =>
  sortBy(quizQuestions, 'order');

interface Params {
  quiz: QuizEditorDetailsFragment;
  state: ReturnType<typeof useQuizEditorState>;
  isQuizPublished: Boolean;
}

const useQuizEditorActions = ({ quiz, state, isQuizPublished }: Params) => {
  const {
    uiState,
    updatedQuestionById,
    updateQuestionByIdActions,
    questions,
    publishActionRef,
    setUiState,
    effectiveQuizQuestions,
    updatedQuizQuestionByQuestionId,
    updatedQuizQuestionByQuestionIdActions,
  } = state;

  const { t } = useTranslation();
  const { showErrorSnackbar, showSnackbar } = useSnackbar();
  const navigation = useNavigation();

  const {
    onCreateQuestion,
    onUpdateQuestions,
    onRemoveQuestion,
    onUpdateQuiz,
    onUpdateQuizQuestions,
    fetching: saving,
  } = useQuizEditorMutations({ quizId: quiz.id });
  const { executeRetakeQuiz } = useRetakeQuiz({
    parentEntityId: quiz.id,
    quizId: quiz.id,
    isPreview: true,
  });

  const { onPublishQuiz, onUpdateQuizData, quizUiValidation } = usePublishQuiz({
    quiz,
    isQuizPublished,
    state,
    onUpdateQuestions,
    onUpdateQuiz,
  });

  const { setQuestion, dirtyQuestionsRef } = useDebouncedQuestionsUpdates({
    onPublish: onPublishQuiz,
    isPublishing: Boolean(uiState.isPublishing),
    onUpdateQuestions,
    updatedQuestionById,
    updateQuestionByIdActions,
  });

  const onCloseLimitedEditingModal = useCallback(() => {
    setUiState({ showLimitingEditingModal: false });
  }, [setUiState]);

  const onClosePublishChangesDialog = useCallback(() => {
    setUiState({ showPublishChangesDialog: false });
  }, [setUiState]);

  const setNudgeState = useCallback(
    (message: string) => {
      const question = questions[uiState.selectedQuestionIndex];
      if (!question) {
        return;
      }

      const nudgeError = {
        type: 'LyearnAINudge',
        message,
      };

      setUiState({ errors: { ...uiState.errors, [question.id]: nudgeError } });
    },
    [questions, setUiState, uiState.errors, uiState.selectedQuestionIndex],
  );

  const onPreview = useEvent(async () => {
    const isValid = await quizUiValidation();

    if (isValid) {
      await executeRetakeQuiz();
      navigation.push(routes.QuizPreviewDevices(quiz.id));
    }
  });

  const onAction: onQuizCreatorAction = useEvent(async (action) => {
    if (!quiz || !questions) {
      return;
    }

    switch (action.type) {
      case QuizCreatorActionsEnum.PublishQuiz: {
        publishActionRef.current = action;
        if (!Object.keys(dirtyQuestionsRef.current).length) {
          onPublishQuiz();
        } else {
          setUiState({ isPublishing: true });
        }
        break;
      }

      case QuizCreatorActionsEnum.UpdateQuiz: {
        const { quiz: updatedQuiz, snackbarProps } = action.payload;
        const res = await onUpdateQuizData(updatedQuiz);
        if (snackbarProps?.message && res && !res.error) {
          showSnackbar({ message: snackbarProps?.message });
        }
        if (!res?.data?.updateQuiz.quiz) {
          setUiState({ isOfSameWorkspace: false });
        }
        break;
      }

      case QuizCreatorActionsEnum.AddQuestions: {
        const previousQuestionsLength = questions.length;
        const { questions: createdQuestions } = await onCreateQuestion(action.payload.questions);
        if (createdQuestions?.length) {
          setUiState({ selectedQuestionIndex: previousQuestionsLength });
        }
        break;
      }

      case QuizCreatorActionsEnum.UpdateQuestion: {
        const { question } = action.payload;
        setQuestion(question);
        break;
      }

      case QuizCreatorActionsEnum.ReorderQuestion: {
        const { fromIndex, toIndex } = action.payload;

        const quizQuestions = getSortedQuestions(effectiveQuizQuestions);
        const questionConfigs = reorderItems({ fromIndex, toIndex, items: quizQuestions }).map(
          (quizQuestion, index) => ({ questionId: quizQuestion.questionId, order: index + 1 }),
        );

        const previous = { ...updatedQuizQuestionByQuestionId };
        questionConfigs.forEach((cfg) => {
          updatedQuizQuestionByQuestionIdActions.set(cfg.questionId, {
            ...(previous[cfg.questionId] ?? {}),
            order: cfg.order,
          });
        });

        const { response } = await onUpdateQuizQuestions(questionConfigs);
        if (response.error || !response.data?.updateQuizQuestions?.success) {
          updatedQuizQuestionByQuestionIdActions.setAll(previous);
        }
        break;
      }

      case QuizCreatorActionsEnum.RemoveQuestion: {
        if (quiz.questions?.length === 1) {
          showErrorSnackbar({ message: t('QuizCreator:Validations.QuizCannotBeEmpty') });
          return;
        }

        const { index } = action.payload;
        const questionId = questions[index].id;

        await onRemoveQuestion(questionId);

        if (index <= uiState.selectedQuestionIndex) {
          setUiState({ selectedQuestionIndex: Math.max(uiState.selectedQuestionIndex - 1, 0) });
        }
        break;
      }

      case QuizCreatorActionsEnum.DuplicateQuestion: {
        const { index } = action.payload;

        const response = await onCreateQuestion([
          { ...adaptToQuestionInput(questions[index]), order: index + 2 },
        ]);

        if (response.questions?.length) {
          setUiState({ selectedQuestionIndex: index + 1 });
        }

        if (response.quizQuestions?.length) {
          const quizQuestions = getSortedQuestions(quiz.quizQuestions);

          const updatedQuizQuestions = splice(
            quizQuestions,
            index + 1,
            0,
            response.quizQuestions[0],
          );

          const questionConfigs = updatedQuizQuestions.map((quizQuestion, index) => ({
            questionId: quizQuestion.questionId,
            order: index + 1,
          }));

          await onUpdateQuizQuestions(questionConfigs);
        }
        break;
      }

      case QuizCreatorActionsEnum.SetSelectedQuestion: {
        setUiState({ selectedQuestionIndex: action.payload.index });
        break;
      }

      case QuizCreatorActionsEnum.SetIsOfDifferentWorkspace: {
        setUiState({ isOfSameWorkspace: false });
        break;
      }

      case QuizCreatorActionsEnum.PublishDialog: {
        setUiState({ showPublishChangesDialog: true });
        break;
      }

      case QuizCreatorActionsEnum.AddSection: {
        const { name } = action.payload;
        const existingSections: QuizSectionInput[] =
          quiz.quizSections?.map((s) => ({
            _id: s._id,
            name: s.name,
            order: s.order,
            description: s.description,
            randomizationConfig: s.randomizationConfig
              ? {
                  questionGenerationType: s.randomizationConfig.questionGenerationType,
                  questionGenerationConfig: s.randomizationConfig.questionGenerationConfig,
                }
              : undefined,
          })) ?? [];

        const newOrder = existingSections.length + 1;
        const newSection: QuizSectionInput = {
          name,
          order: newOrder,
        };

        await onUpdateQuizData({ quizSections: [...existingSections, newSection] });
        break;
      }

      case QuizCreatorActionsEnum.RemoveSection: {
        const { sectionId } = action.payload;
        const filteredSections: QuizSectionInput[] =
          quiz.quizSections
            ?.filter((s) => s._id !== sectionId)
            .map((s, index) => ({
              _id: s._id,
              name: s.name,
              order: index + 1,
              description: s.description,
              randomizationConfig: s.randomizationConfig
                ? {
                    questionGenerationType: s.randomizationConfig.questionGenerationType,
                    questionGenerationConfig: s.randomizationConfig.questionGenerationConfig,
                  }
                : undefined,
            })) ?? [];

        const affectedQuizQuestions = quiz.quizQuestions.filter((qq) => qq.sectionId === sectionId);
        if (affectedQuizQuestions.length > 0) {
          const clearSectionConfigs = affectedQuizQuestions.map((qq) => ({
            questionId: qq.questionId,
            sectionId: null,
          }));
          await onUpdateQuizQuestions(clearSectionConfigs);
        }
        await onUpdateQuizData({ quizSections: filteredSections });
        break;
      }

      case QuizCreatorActionsEnum.UpdateSection: {
        const { sectionId, updates } = action.payload;
        const updatedSections: QuizSectionInput[] =
          quiz.quizSections?.map((s) => {
            const base: QuizSectionInput = {
              _id: s._id,
              name: s.name,
              order: s.order,
              description: s.description,
              randomizationConfig: s.randomizationConfig
                ? {
                    questionGenerationType: s.randomizationConfig.questionGenerationType,
                    questionGenerationConfig: s.randomizationConfig.questionGenerationConfig,
                  }
                : undefined,
            };
            return s._id === sectionId ? { ...base, ...updates } : base;
          }) ?? [];

        await onUpdateQuizData({ quizSections: updatedSections });
        break;
      }

      case QuizCreatorActionsEnum.ReorderSection: {
        const { fromIndex, toIndex } = action.payload;
        const sortedSections = sortBy(quiz.quizSections ?? [], 'order');
        const reorderedSections = reorderItems({
          fromIndex,
          toIndex,
          items: sortedSections,
        });
        const updatedSections: QuizSectionInput[] = reorderedSections.map((s, index) => ({
          _id: s._id,
          name: s.name,
          order: index + 1,
          description: s.description,
          randomizationConfig: s.randomizationConfig
            ? {
                questionGenerationType: s.randomizationConfig.questionGenerationType,
                questionGenerationConfig: s.randomizationConfig.questionGenerationConfig,
              }
            : undefined,
        }));
        await onUpdateQuizData({ quizSections: updatedSections });
        break;
      }

      case QuizCreatorActionsEnum.MoveQuestionToSection: {
        const { questionId, sectionId, toIndex } = action.payload;

        const sorted = getSortedQuestions(effectiveQuizQuestions);
        const fromIndex = sorted.findIndex((qq) => qq.questionId === questionId);
        if (fromIndex < 0) {
          break;
        }

        setUiState({ isMovingQuestion: true, movingQuestionId: questionId });

        let targetIndex = typeof toIndex === 'number' ? toIndex : -1;
        if (targetIndex < 0) {
          // menu move: place at end of target section (or end of list for unsectioned)
          const lastIndexInTarget = [...sorted]
            .map((qq, idx) => ({ qq, idx }))
            .filter(({ qq }) => (qq.sectionId ?? null) === sectionId)
            .map(({ idx }) => idx)
            .pop();
          targetIndex =
            typeof lastIndexInTarget === 'number' ? lastIndexInTarget + 1 : sorted.length;
        }

        const moved = sorted[fromIndex];
        const remaining = sorted.filter((_, idx) => idx !== fromIndex);
        const clampedTarget = Math.max(0, Math.min(targetIndex, remaining.length));
        const reordered = splice(remaining, clampedTarget, 0, {
          ...moved,
          sectionId,
        });

        const questionConfigs = reordered.map((qq, index) => ({
          questionId: qq.questionId,
          order: index + 1,
          sectionId: qq.questionId === questionId ? sectionId : qq.sectionId ?? null,
        }));

        const previous = { ...updatedQuizQuestionByQuestionId };
        questionConfigs.forEach((cfg) => {
          updatedQuizQuestionByQuestionIdActions.set(cfg.questionId, {
            ...(previous[cfg.questionId] ?? {}),
            order: cfg.order,
            ...(cfg.questionId === questionId ? { sectionId } : {}),
          });
        });

        try {
          const { response } = await onUpdateQuizQuestions(questionConfigs);
          if (response.error || !response.data?.updateQuizQuestions?.success) {
            updatedQuizQuestionByQuestionIdActions.setAll(previous);
            showErrorSnackbar({
              message: response.error?.message ?? t('Common:Error.SomethingWentWrong'),
            });
          }
        } finally {
          setUiState({ isMovingQuestion: false, movingQuestionId: undefined });
        }
        break;
      }

      default: {
        break;
      }
    }
  });

  const onPublish = useCallback(() => {
    const contentType = t(config[ResourceTypeEnum.Quiz].label.title);

    if (quiz.status === ResourceStatusEnum.Draft) {
      onAction({
        type: QuizCreatorActionsEnum.PublishQuiz,
        payload: {
          publishConfig: { versionType: ResourceVersionTypeEnum.Major },
          snackbarProps: {
            message: t('Common:Form.ContentTypePublishedSuccessfully', { contentType }),
          },
        },
      });
    } else {
      onAction({
        type: QuizCreatorActionsEnum.PublishDialog,
      });
    }
  }, [onAction, quiz.status, t]);

  const onPublishSubmit: onPublishType = useCallback(
    (publishConfig: PublishContentConfig) => {
      onAction({
        type: QuizCreatorActionsEnum.PublishQuiz,
        payload: {
          publishConfig,
          snackbarProps: {
            message:
              publishConfig.versionType === ResourceVersionTypeEnum.Major
                ? t('Common:Form.ChangesPublishedSuccessfully')
                : t('Common:Form.UpdatedExistingVersionSuccessfully'),
          },
        },
      });
      return new Promise(() => {});
    },
    [onAction, t],
  );

  return {
    onAction,
    onCloseLimitedEditingModal,
    onClosePublishChangesDialog,
    setNudgeState,
    onPublishSubmit,
    onPublish,
    onPreview,
    saving,
  };
};

export default useQuizEditorActions;
